import { useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import toast from "react-hot-toast";
import { supabase } from "../../lib/supabase";
import { queryKeys } from "../../lib/queryKeys";
import { useAuth } from "../../contexts/AuthContext";
import { uploadTaskAttachment as uploadToStorage } from "../../lib/storage";
import { TaskAttachment, SubtaskAttachment, Task } from "../../types";

const ATTACHMENTS_BUCKET = "task-attachments";

export function useUploadTaskAttachment() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return async (taskId: string, file: File): Promise<TaskAttachment | null> => {
    if (!user) return null;

    try {
      const uploadResult = await uploadToStorage(file, user.id, taskId);

      const { data, error } = await supabase
        .from("task_attachments")
        .insert({
          task_id: taskId,
          file_name: uploadResult.file_name,
          file_path: uploadResult.file_path,
          file_size: uploadResult.file_size,
          mime_type: uploadResult.mime_type,
          storage_url: uploadResult.storage_url,
        })
        .select()
        .single();

      if (error) {
        // DB insert failed — roll back the storage upload to prevent orphaned files.
        const { error: rollbackError } = await supabase.storage
          .from(ATTACHMENTS_BUCKET)
          .remove([uploadResult.file_path]);
        if (rollbackError) {
          Sentry.captureException(rollbackError, {
            extra: { mutation: "useUploadTaskAttachment/rollback", taskId },
          });
        }
        Sentry.captureException(error, {
          extra: { mutation: "useUploadTaskAttachment", taskId },
        });
        toast.error("Failed to save attachment. Please try again.");
        return null;
      }

      queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
      return data as TaskAttachment;
    } catch (err) {
      // Catches validation errors (invalid type/size) and storage upload failures.
      Sentry.captureException(err, {
        extra: { mutation: "useUploadTaskAttachment/upload", taskId },
      });
      toast.error("Failed to upload attachment. Please try again.");
      return null;
    }
  };
}

export function useUploadSubtaskAttachment() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return async (subtaskId: string, file: File): Promise<SubtaskAttachment | null> => {
    if (!user) return null;

    const tasks = queryClient.getQueryData<Task[]>(queryKeys.tasks) ?? [];
    const parentTask = tasks.find((t) =>
      t.subtasks?.some((st) => st.id === subtaskId)
    );
    if (!parentTask) return null;

    try {
      const uploadResult = await uploadToStorage(file, user.id, parentTask.id);

      const { data, error } = await supabase
        .from("subtask_attachments")
        .insert({
          subtask_id: subtaskId,
          file_name: uploadResult.file_name,
          file_path: uploadResult.file_path,
          file_size: uploadResult.file_size,
          mime_type: uploadResult.mime_type,
          storage_url: uploadResult.storage_url,
        })
        .select()
        .single();

      if (error) {
        const { error: rollbackError } = await supabase.storage
          .from(ATTACHMENTS_BUCKET)
          .remove([uploadResult.file_path]);
        if (rollbackError) {
          Sentry.captureException(rollbackError, {
            extra: { mutation: "useUploadSubtaskAttachment/rollback", subtaskId },
          });
        }
        Sentry.captureException(error, {
          extra: { mutation: "useUploadSubtaskAttachment", subtaskId },
        });
        toast.error("Failed to save attachment. Please try again.");
        return null;
      }

      queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
      return data as SubtaskAttachment;
    } catch (err) {
      Sentry.captureException(err, {
        extra: { mutation: "useUploadSubtaskAttachment/upload", subtaskId },
      });
      toast.error("Failed to upload attachment. Please try again.");
      return null;
    }
  };
}
